import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../../hooks/useApi';

const POLL_INTERVAL = 30000;
const BASE_TITLE = document.title;
const PULSE_DURATION = 2800; // must exceed the 3x 0.9s CSS animation iterations (styles.css .notif-badge-pulse)

// Favicon badging — draws the count onto the static favicon.svg via canvas and
// swaps the <link rel="icon"> href to the result, so a pending notification is
// visible even when this tab isn't focused (the tab title change alone is easy
// to miss if several tabs are open). Cached so we don't reload/redraw on every
// 30s poll when the count hasn't changed.
let _faviconBaseImg = null;
let _faviconBaseImgPromise = null;
function loadFaviconBaseImage() {
  if (_faviconBaseImgPromise) return _faviconBaseImgPromise;
  _faviconBaseImgPromise = new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => { _faviconBaseImg = img; resolve(img); };
    img.onerror = reject;
    img.src = '/favicon.svg';
  });
  return _faviconBaseImgPromise;
}
let _lastBadgedCount = undefined;
async function setFaviconBadge(count) {
  if (count === _lastBadgedCount) return;
  const link = document.querySelector('link[rel="icon"]');
  if (!link) return;
  if (!count) { link.href = '/favicon.svg'; _lastBadgedCount = count; return; }
  try {
    const img = _faviconBaseImg || await loadFaviconBaseImage();
    const size = 64;
    const canvas = document.createElement('canvas');
    canvas.width = size; canvas.height = size;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, size, size);
    const r = 13;
    const cx = size - r - 2;
    const cy = r + 2;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fillStyle = '#EF4444';
    ctx.fill();
    ctx.lineWidth = 2.5;
    ctx.strokeStyle = '#ffffff';
    ctx.stroke();
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 22px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(count > 9 ? '9+' : String(count), cx, cy + 1);
    link.href = canvas.toDataURL('image/png');
    _lastBadgedCount = count;
  } catch (_) { /* non-fatal — favicon just stays unbadged */ }
}

// Every type the notifications endpoint can emit needs an entry here. Miss one
// and it falls through to DEFAULT_META, which is how a panel of twenty-three
// items ended up showing eight chips all reading "Notification": a clearance
// expiry, a contract ending and a hardware request were indistinguishable.
// If a type is added server-side, add it here too.
const TYPE_META = {
  // Needs a decision from you
  leave_approval:    { icon: '🗓', label: 'Leave Request',      border: 'border-l-blue-500',    bg: 'bg-blue-50',    badge: 'bg-blue-100 text-blue-700'      },
  ot_approval:       { icon: '⏱', label: 'Overtime',            border: 'border-l-purple-500',  bg: 'bg-purple-50',  badge: 'bg-purple-100 text-purple-700'  },
  hardware_request:  { icon: '🖥', label: 'Hardware Request',    border: 'border-l-indigo-500',  bg: 'bg-indigo-50',  badge: 'bg-indigo-100 text-indigo-700'  },
  detail_change:     { icon: '📝', label: 'Details Changed',     border: 'border-l-sky-500',     bg: 'bg-sky-50',     badge: 'bg-sky-100 text-sky-700'        },
  probation_review:  { icon: '🧭', label: 'Probation Review',    border: 'border-l-violet-500',  bg: 'bg-violet-50',  badge: 'bg-violet-100 text-violet-700'  },
  plow_access_request: { icon: '🔑', label: 'PLOW Access',       border: 'border-l-amber-500',   bg: 'bg-amber-50',   badge: 'bg-amber-100 text-amber-700'    },

  // Delivery and commercial
  missed_milestone:  { icon: '🚨', label: 'Missed Milestone',    border: 'border-l-red-500',     bg: 'bg-red-50',     badge: 'bg-red-100 text-red-700'        },
  ready_to_bill:     { icon: '💷', label: 'Ready to Bill',       border: 'border-l-emerald-500', bg: 'bg-emerald-50', badge: 'bg-emerald-100 text-emerald-700'},
  overdue_task:      { icon: '⚠️', label: 'Overdue Task',        border: 'border-l-amber-500',   bg: 'bg-amber-50',   badge: 'bg-amber-100 text-amber-700'    },
  plan_task_overdue: { icon: '📉', label: 'Plan Task Late',      border: 'border-l-red-500',     bg: 'bg-red-50',     badge: 'bg-red-100 text-red-700'        },
  plan_task_starting:{ icon: '📅', label: 'Plan Task Starting',  border: 'border-l-sky-500',     bg: 'bg-sky-50',     badge: 'bg-sky-100 text-sky-700'        },
  sow_query:         { icon: '💬', label: 'SOW Query',           border: 'border-l-teal-500',    bg: 'bg-teal-50',    badge: 'bg-teal-100 text-teal-700'      },
  action:            { icon: '💬', label: 'Action',              border: 'border-l-teal-500',    bg: 'bg-teal-50',    badge: 'bg-teal-100 text-teal-700'      },
  action_completed:  { icon: '✅', label: 'Action Completed',    border: 'border-l-emerald-500', bg: 'bg-emerald-50', badge: 'bg-emerald-100 text-emerald-700'},
  my_list_reminder:  { icon: '🔔', label: 'My List Reminder',   border: 'border-l-amber-500',   bg: 'bg-amber-50',   badge: 'bg-amber-100 text-amber-700'    },
  my_list_shared:    { icon: '📌', label: 'Shared Note',         border: 'border-l-sky-500',     bg: 'bg-sky-50',     badge: 'bg-sky-100 text-sky-700'        },
  po_delivered:      { icon: '📦', label: 'Order Delivered',     border: 'border-l-emerald-500', bg: 'bg-emerald-50', badge: 'bg-emerald-100 text-emerald-700'},
  suggestion_status: { icon: '💡', label: 'Suggestion Updated',  border: 'border-l-yellow-500',  bg: 'bg-yellow-50',  badge: 'bg-yellow-100 text-yellow-700'  },
  suggestion_note:   { icon: '📣', label: 'Suggestion Update',   border: 'border-l-yellow-500',  bg: 'bg-yellow-50',  badge: 'bg-yellow-100 text-yellow-700'  },

  // Expiries and dates that creep up on you
  clearance_expiry:  { icon: '🔐', label: 'Clearance Expiring',  border: 'border-l-rose-500',    bg: 'bg-rose-50',    badge: 'bg-rose-100 text-rose-700'      },
  pass_expiry:       { icon: '🪪', label: 'Pass Expiring',       border: 'border-l-rose-500',    bg: 'bg-rose-50',    badge: 'bg-rose-100 text-rose-700'      },
  contract_ending:   { icon: '📄', label: 'Contract Ending',     border: 'border-l-orange-500',  bg: 'bg-orange-50',  badge: 'bg-orange-100 text-orange-700'  },

  // Leave, yours and your team's
  leave_unused:            { icon: '🏖', label: 'Unused Holiday',      border: 'border-l-cyan-500', bg: 'bg-cyan-50', badge: 'bg-cyan-100 text-cyan-700' },
  leave_unused_team:       { icon: '🏖', label: 'Team Holiday',        border: 'border-l-cyan-500', bg: 'bg-cyan-50', badge: 'bg-cyan-100 text-cyan-700' },
  carry_over_expiring:     { icon: '⏳', label: 'Carry-Over Expiring', border: 'border-l-amber-500',bg: 'bg-amber-50',badge: 'bg-amber-100 text-amber-700'},
  leave_tenure_grant:      { icon: '🎉', label: 'Extra Leave',         border: 'border-l-lime-500', bg: 'bg-lime-50', badge: 'bg-lime-100 text-lime-700' },
  leave_carry_over:        { icon: '↪️', label: 'Leave Carried Over',  border: 'border-l-lime-500', bg: 'bg-lime-50', badge: 'bg-lime-100 text-lime-700' },
  leave_carry_over_expired:{ icon: '⌛', label: 'Carry-Over Lost',     border: 'border-l-gray-400', bg: 'bg-gray-50', badge: 'bg-gray-100 text-gray-600' },
};
const DEFAULT_META = { icon: '•', label: 'Other', border: 'border-l-gray-300', bg: 'bg-gray-50', badge: 'bg-gray-100 text-gray-600' };

// Twenty-three items in one undifferentiated scroll is the complaint. Banding
// them by how much they want from you turns the list into three short ones.
const BANDS = [
  { key: 'high',   label: 'Needs action',   hint: 'Somebody is waiting on you' },
  { key: 'medium', label: 'Worth a look',   hint: '' },
  { key: 'low',    label: 'For information', hint: '' },
];
const BAND_RANK = { high: 0, medium: 1, low: 2 };
const bandOf = (item) => (BAND_RANK[item.priority] !== undefined ? item.priority : 'medium');

// Format YYYY-MM-DD → "27 Mar 2026"
function fmtDate(s) {
  if (!s) return '';
  const d = new Date(s + 'T00:00:00');
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

// Replace all YYYY-MM-DD occurrences in a string with dd Mon yyyy
function formatDatesInText(text) {
  if (!text) return text;
  return text.replace(/\b(\d{4}-\d{2}-\d{2})\b/g, (_, d) => fmtDate(d));
}

// Days overdue (positive = overdue)
function daysOverdue(dateStr) {
  if (!dateStr) return null;
  const diff = Math.floor((Date.now() - new Date(dateStr + 'T00:00:00').getTime()) / 86400000);
  return diff > 0 ? diff : null;
}

// Parse structured info from notification message
function parseNotification(item) {
  const msg = item.message || '';
  if (item.type === 'missed_milestone') {
    // "AV-402543 · AVM-01 — Description (forecast 2026-04-13)"
    const dateMatch = msg.match(/\b(\d{4}-\d{2}-\d{2})\b/);
    const date = dateMatch ? dateMatch[1] : null;
    const bodyText = msg.replace(/\s*\(forecast[^)]*\)/, '').trim();
    const overdue = daysOverdue(date);
    return { body: bodyText, date, overdue };
  }
  if (item.type === 'leave_approval') {
    // "Carl Hamill — annual leave, 2026-06-15 to 2026-06-18 (3d)"
    const body = formatDatesInText(msg);
    return { body, date: null, overdue: null };
  }
  if (item.type === 'ot_approval') {
    // "Carl Hamill — overtime on 2026-06-14"
    return { body: formatDatesInText(msg), date: null, overdue: null };
  }
  if (item.type === 'overdue_task') {
    // "Task name (due 2026-06-01)"
    const dateMatch = msg.match(/\b(\d{4}-\d{2}-\d{2})\b/);
    const date = dateMatch ? dateMatch[1] : null;
    const overdue = daysOverdue(date);
    return { body: formatDatesInText(msg), date, overdue };
  }
  return { body: formatDatesInText(msg), date: null, overdue: null };
}

export default function NotificationBell({ onNavigate }) {
  const [open, setOpen]           = useState(false);
  const [items, setItems]         = useState([]);
  const [count, setCount]         = useState(0);
  // Local cache only used to hide an item instantly on click, ahead of the
  // next poll — the server (notification_dismissals table) is the source of
  // truth, so a dismissal follows the user across devices/browsers instead of
  // being stuck in one machine's localStorage.
  const [dismissed, setDismissed] = useState(() => {
    try { return JSON.parse(localStorage.getItem('av_dismissed_notifs') || '[]'); }
    catch { return []; }
  });
  // Which type chips are switched on. Empty means "everything", so the panel
  // opens showing the lot and a filter is something you choose, not something
  // you have to clear.
  const [typeFilter, setTypeFilter] = useState([]);
  const panelRef = useRef(null);
  const prevCountRef = useRef(0);
  const pulseTimerRef = useRef(null);
  const [pulse, setPulse] = useState(false);

  const fetchNotifications = useCallback(async () => {
    try {
      const data = await api.get('notifications');
      const allItems = data?.items || [];
      const visible = allItems.filter(n => n.type === 'missed_milestone' || !dismissed.includes(n.id));
      setItems(visible);
      setCount(visible.length);
      // Pulse once whenever the pending count goes UP (new notification arrived,
      // or the very first load if there's already something pending) — not on
      // every poll, and not when it only goes down from dismissing/resolving.
      if (visible.length > prevCountRef.current) {
        setPulse(true);
        clearTimeout(pulseTimerRef.current);
        pulseTimerRef.current = setTimeout(() => setPulse(false), PULSE_DURATION);
      }
      prevCountRef.current = visible.length;
    } catch (_) {}
  }, [dismissed]);

  useEffect(() => {
    fetchNotifications();
    const timer = setInterval(fetchNotifications, POLL_INTERVAL);
    return () => { clearInterval(timer); clearTimeout(pulseTimerRef.current); };
  }, [fetchNotifications]);

  // Surface the pending count where it's visible even when this tab isn't
  // focused — the badge on the bell only helps if you're looking at this tab.
  // No unmount cleanup needed: the bell lives in the persistent header for the
  // whole session, it doesn't mount/unmount per page.
  useEffect(() => {
    document.title = count > 0 ? `(${count > 99 ? '99+' : count}) ${BASE_TITLE}` : BASE_TITLE;
    setFaviconBadge(count);
  }, [count]);

  useEffect(() => {
    const onVisible = () => { if (!document.hidden) fetchNotifications(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [fetchNotifications]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Missed milestones and open actions can't be hand-dismissed: they stay until
  // the underlying thing is actioned. An action clears itself from the bell once
  // it's resolved or you clear it from My Tasks (the live recompute drops it),
  // so clicking through to read it must NOT make it vanish.
  const canDismiss = (item) => item.type !== 'missed_milestone' && item.type !== 'action' && item.type !== 'plow_access_request';

  const persistDismiss = (ids) => {
    api.post('notifications/dismiss', { ids }).catch(() => {
      // Non-fatal: worst case the item reappears on the next poll and the
      // user dismisses it again. Local state below already hid it for now.
    });
  };

  const dismiss = (id) => {
    const item = items.find(n => n.id === id);
    if (!item || !canDismiss(item)) return;
    const next = [...dismissed, id];
    setDismissed(next);
    localStorage.setItem('av_dismissed_notifs', JSON.stringify(next));
    setItems(prev => prev.filter(n => n.id !== id));
    setCount(prev => Math.max(0, prev - 1));
    persistDismiss([id]);
  };

  // Scoped to what is on screen. With a type filter active, "clear" that wiped
  // everything including the items you had filtered out would be a nasty
  // surprise you could not undo.
  const dismissAll = (scope) => {
    const dismissible = (scope || items).filter(canDismiss);
    if (!dismissible.length) return;
    const ids = dismissible.map(n => n.id);
    const next = [...dismissed, ...ids];
    setDismissed(next);
    localStorage.setItem('av_dismissed_notifs', JSON.stringify(next));
    const clearing = new Set(ids);
    const remaining = items.filter(n => !clearing.has(n.id));
    setItems(remaining);
    setCount(prev => Math.max(0, prev - dismissible.length));
    if (remaining.length === 0) setOpen(false);
    persistDismiss(ids);
  };

  const handleItemClick = (item) => {
    if (onNavigate && item.link) onNavigate(item.link.replace(/^\//, ''), item.entity_id || undefined, item.sub || undefined);
    // Only hide on click if it's a dismissible type — actions/missed milestones
    // persist until they're actually resolved.
    if (canDismiss(item)) dismiss(item.id);
    setOpen(false);
  };

  // Group by type for the filter chips, busiest first so the chip worth
  // clicking is the one nearest the left.
  const byType = {};
  for (const item of items) byType[item.type] = (byType[item.type] || 0) + 1;
  const chipTypes = Object.entries(byType).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));

  const toggleType = (type) =>
    setTypeFilter(prev => (prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]));

  const filtered = typeFilter.length ? items.filter(n => typeFilter.includes(n.type)) : items;

  // Most urgent first, then the most overdue, then newest. Within a band the
  // thing that has been waiting longest should be the thing you see first.
  const sorted = [...filtered].sort((a, b) => {
    const band = BAND_RANK[bandOf(a)] - BAND_RANK[bandOf(b)];
    if (band !== 0) return band;
    const oa = parseNotification(a).overdue || 0;
    const ob = parseNotification(b).overdue || 0;
    if (oa !== ob) return ob - oa;
    return String(b.created_at || '').localeCompare(String(a.created_at || ''));
  });

  // Band headers are emitted inline as the list is rendered.
  const bandCounts = {};
  for (const item of sorted) bandCounts[bandOf(item)] = (bandCounts[bandOf(item)] || 0) + 1;
  const seenBands = new Set();

  return (
    <div className="relative" ref={panelRef}>
      {/* Sized and coloured to match the dark-mode and Improvement Suggestions
          buttons either side of it in the header (App.jsx). It used to carry a
          white icon on a transparent ground, from when the header was navy —
          against today's light header that left a bell you could barely see,
          sitting at a different size to its neighbours. The count no longer
          fits inside a 28px box beside the icon, so it moves to a badge pinned
          on the corner, which is where a notification count belongs anyway. */}
      <button
        onClick={() => setOpen(o => !o)}
        title="Notifications"
        aria-label={`Notifications${count > 0 ? ` — ${count} pending` : ''}`}
        style={{
          width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
          borderRadius: 6, border: '1px solid #E5E7EB', background: '#F9FAFB', cursor: 'pointer',
          color: '#6B7280', flexShrink: 0, position: 'relative', padding: 0,
        }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
          <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
        </svg>
        {count > 0 && (
          <span
            className={`bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center leading-none ${pulse ? 'notif-badge-pulse' : ''}`}
            style={{ position: 'absolute', top: -5, right: -5, minWidth: 15, height: 15, padding: '0 3px', border: '1.5px solid #fff' }}
          >
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-9 w-96 max-w-[calc(100vw-1rem)] bg-white rounded-xl shadow-2xl border border-gray-200 z-50 overflow-hidden">
          {/* Header */}
          <div className="px-4 py-3 border-b bg-gray-50 flex items-center justify-between">
            <div>
              <span className="font-bold text-gray-900 text-[13px]">Notifications</span>
              {count > 0 && (
                <span className="ml-2 text-[11px] text-gray-500">
                  {typeFilter.length
                    ? `showing ${sorted.length} of ${count}`
                    : `${count} pending action${count !== 1 ? 's' : ''}`}
                </span>
              )}
            </div>
            <div className="flex items-center gap-3">
              {sorted.some(canDismiss) && (
                <button onClick={() => dismissAll(sorted)} className="text-[11px] text-gray-400 hover:text-gray-700 font-medium">
                  {typeFilter.length ? 'Clear these' : 'Clear dismissible'}
                </button>
              )}
              <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
            </div>
          </div>

          {/* Type filter chips — click to narrow the list to one kind of thing */}
          {count > 0 && (
            <div className="px-4 py-2 border-b bg-white flex flex-wrap gap-1.5">
              <button
                onClick={() => setTypeFilter([])}
                aria-pressed={typeFilter.length === 0}
                className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border transition-colors ${
                  typeFilter.length === 0
                    ? 'bg-navy text-white border-navy'
                    : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'
                }`}
              >
                All ({items.length})
              </button>
              {chipTypes.map(([type, n]) => {
                const meta = TYPE_META[type] || DEFAULT_META;
                const on = typeFilter.includes(type);
                return (
                  <button
                    key={type}
                    onClick={() => toggleType(type)}
                    aria-pressed={on}
                    title={on ? `Stop filtering by ${meta.label}` : `Show only ${meta.label}`}
                    className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border transition-colors ${meta.badge} ${
                      on ? 'border-current ring-1 ring-current' : 'border-transparent opacity-70 hover:opacity-100'
                    }`}
                  >
                    {meta.icon} {meta.label} ({n})
                  </button>
                );
              })}
            </div>
          )}

          {/* Items */}
          <div className="max-h-[420px] overflow-y-auto divide-y divide-gray-100">
            {items.length === 0 ? (
              <div className="p-8 text-center text-gray-400 text-sm">
                <div className="text-3xl mb-2">🎉</div>
                All clear — no pending actions
              </div>
            ) : sorted.length === 0 ? (
              <div className="p-8 text-center text-gray-400 text-[11px]">
                Nothing of that kind right now.
                <button onClick={() => setTypeFilter([])} className="block mx-auto mt-2 text-navy font-semibold hover:underline">
                  Show all {items.length}
                </button>
              </div>
            ) : (
              sorted.map(item => {
                const meta = TYPE_META[item.type] || DEFAULT_META;
                const { body, date, overdue } = parseNotification(item);
                const band = bandOf(item);
                const firstOfBand = !seenBands.has(band) && seenBands.add(band);
                const bandMeta = BANDS.find(b => b.key === band);
                return (
                  <React.Fragment key={item.id}>
                  {firstOfBand && (
                    <div className="px-3 py-1 bg-gray-50 flex items-baseline gap-2 sticky top-0 z-10">
                      <span className="text-[9px] font-bold uppercase tracking-wider text-gray-500">{bandMeta?.label}</span>
                      <span className="text-[9px] text-gray-400">{bandCounts[band]}</span>
                      {bandMeta?.hint && <span className="text-[9px] text-gray-300 ml-auto">{bandMeta.hint}</span>}
                    </div>
                  )}
                  <div
                    className={`flex items-stretch group hover:bg-gray-50 transition-colors cursor-pointer border-l-4 ${meta.border}`}
                    onClick={() => handleItemClick(item)}
                  >
                    <div className="flex-1 min-w-0 px-3 py-2.5">
                      {/* Type label */}
                      <div className="flex items-center gap-1.5 mb-1">
                        <span className="text-[10px]">{meta.icon}</span>
                        <span className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${meta.badge}`}>
                          {meta.label}
                        </span>
                        {overdue != null && (
                          <span className="text-[10px] font-semibold text-red-500 ml-auto">{overdue}d overdue</span>
                        )}
                      </div>
                      {/* Body */}
                      <div className="text-[11px] font-semibold text-gray-800 leading-snug break-words">
                        {body}
                      </div>
                      {/* Date */}
                      {date && (
                        <div className="text-[10px] text-gray-400 mt-0.5">
                          {item.type === 'missed_milestone' ? 'Forecast' : 'Due'}: {fmtDate(date)}
                        </div>
                      )}
                    </div>
                    {canDismiss(item) && (
                      <button
                        onClick={e => { e.stopPropagation(); dismiss(item.id); }}
                        className="px-2 text-gray-300 hover:text-red-400 active:text-red-400 flex-shrink-0 text-lg"
                        title="Dismiss"
                      >&times;</button>
                    )}
                  </div>
                  </React.Fragment>
                );
              })
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-2 border-t bg-gray-50 flex items-center justify-between">
            <span className="text-[10px] text-gray-400">Missed milestones &amp; actions stay until resolved — they cannot be dismissed</span>
            <button onClick={fetchNotifications} className="text-[11px] text-gray-400 hover:text-gray-700 font-medium">Refresh</button>
          </div>
        </div>
      )}
    </div>
  );
}
